import React, { ReactElement } from 'react';
import {
  enumToKeyValueArray,
  IListJobFetchParams,
  IListOrderFetchParams,
  IResourceFetchParams,
  MN_JOBS,
  MN_ORDERS,
  MN_RESOURCES,
  MN_TIMESHEET,
} from '@jobcost-monorepo/crud';
import { checkIfAdmin, cn, getDaysOfWeek } from '@jobcost-monorepo/utils';
import {
  Checkbox,
  DatePicker,
  Dropdown,
  HoursInputForm,
  TextAreaForm,
} from '@jobcost-monorepo/ui';
import { useController, useFormContext } from 'react-hook-form';
import { IReplicateForm } from '../index';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import DropdownCrud from '@jobcost-monorepo/crud/ui/DropdownCrud';
import { MarkTypeEnum } from '@jobcost-monorepo/database/prisma/enums';

interface Props {
  handleChangeCheckbox: (value: number, checked: boolean) => void;
}

const ReplicateFormBody = ({ handleChangeCheckbox }: Props): ReactElement => {
  const isAdmin = checkIfAdmin();
  const daysOfWeek = getDaysOfWeek('it');
  const { getValues, control } = useFormContext<IReplicateForm>();
  const {
    formState: { errors },
  } = useController<IReplicateForm>({ control, name: 'days' });
  const { t } = useTranslation(['errors', 'timesheets']);
  const error = errors?.['days']?.message ?? '';
  const errorTranslated = t(error as string);

  const days = getValues('days');

  return (
    <div className="flex flex-col w-full overflow-y-auto px-4">
      <div
        key={`timesheets-resourceId`}
        className="flex flex-row w-full flex-1 space-x-2"
      >
        <div key={'resourceId'} className={'flex flex-1 w-full'}>
          <DropdownCrud<IReplicateForm, IResourceFetchParams>
            moduleName={MN_RESOURCES}
            linkedField={'resourceId'}
            linkedFieldForDefaultValue={'resource.name'}
            label={t('form.resourceId', { ns: 'timesheets' })}
            forceDisabled={!isAdmin}
          />
        </div>
      </div>
      <div
        key={`timesheets-jobs-and-order`}
        className="flex flex-row w-full flex-1 space-x-2"
      >
        <div key={'jobId'} className={'flex flex-1 w-full'}>
          <DropdownCrud<IReplicateForm, IListJobFetchParams>
            moduleName={MN_JOBS}
            linkedField={'jobId'}
            linkedFieldForDefaultValue={'job.name'}
            label={t('form.jobId', { ns: 'timesheets' })}
            dependsOnFields={['resourceId']}
            dependsOnFieldsForSearch={['resourceId']}
          />
        </div>
        <div key={'orderId'} className={cn('flex flex-1 w-full')}>
          <DropdownCrud<IReplicateForm, IListOrderFetchParams>
            moduleName={MN_ORDERS}
            linkedField={'orderId'}
            linkedFieldForDefaultValue={'order.name'}
            label={t('form.orderId', { ns: 'timesheets' })}
            dependsOnFields={['jobId', 'resourceId']}
            dependsOnFieldsForSearch={['jobId', 'resourceId']}
          />
        </div>
      </div>
      <div
        key={`timesheets-hours`}
        className="flex flex-row w-full flex-1 space-x-2"
      >
        <div key={'workedHours'} className={cn('flex flex-1 w-full')}>
          <HoursInputForm
            name={'workedHours'}
            errorName={'workedHours'}
            label={t('form.workedHours', { ns: 'timesheets' })}
            type={'number'}
            disabled={false}
            width={'w-full'}
          />
        </div>
        <div key={'markType'} className={cn('flex flex-1 w-full')}>
          <Dropdown
            name={'markType'}
            errorName={'markType'}
            label={t('form.markType', { ns: 'timesheets' })}
            items={enumToKeyValueArray(MarkTypeEnum, 'markType', MN_TIMESHEET)}
            searchable
            disabled={false}
            width={'w-full'}
          />
        </div>
      </div>
      <div
        key={`timesheets-note`}
        className="flex flex-row w-full flex-1 space-x-2"
      >
        <div key={'note'} className={cn('flex flex-1 w-full')}>
          <TextAreaForm
            name={'note'}
            errorName={'note'}
            label={t('form.note', { ns: 'timesheets' })}
            readOnly={false}
            inputWidth={'w-full'}
            disabled={false}
          />
        </div>
      </div>
      {daysOfWeek.map((day) => (
        <div key={day.id} className="flex flex-row mx-3 items-center">
          <Checkbox
            value={day.id}
            defaultChecked={days.includes(Number(day.id))}
            onCheckedChange={(checked) =>
              handleChangeCheckbox(Number(day.id), Boolean(checked))
            }
          />
          <label className="ml-2">{day.name}</label>
        </div>
      ))}
      <div className="pointer-events-none inset-y-0 right-0 flex items-center pr-3">
        {errorTranslated ? (
          <div className="flex flex-row text-red mt-1">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red mr-1"
              aria-hidden="true"
            />
            {errorTranslated}
          </div>
        ) : (
          <span className="h-5 w-5 " />
        )}
      </div>
      <div className="flex flex-row w-full flex-1 space-x-2 h-5">
        <DatePicker
          name="startDate"
          errorName="startDate"
          label={t('replicate.start', { ns: 'timesheets' })}
          inputWidth="w-full"
        />
        <DatePicker
          name="endDate"
          errorName="endDate"
          label={t('replicate.end', { ns: 'timesheets' })}
          inputWidth="w-full"
        />
      </div>
    </div>
  );
};

export default ReplicateFormBody;
